import React, { useCallback } from 'react';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import AdminCreatorForm, { AdminCreatorFormValues } from './AdminCreatorForm/AdminCreatorForm';
import { paymentTermOptions, preferredPaymentOptions } from '../../../../utils/constants';
import { FormikHelpers } from 'formik';
import { Modal } from '@round/ui-kit';
import { showNotification } from '../../../../utils/showNotification';
import {
    ApiResponse,
    InfluencerCost,
    isCampaignType,
    isPlatform,
    postAdminRetrieveInfluencer,
    postAdminInfluencerCost,
    WriteAdminInfluencerResponse,
} from '@round/api';
import { mapApiErrorsToFormikErrors } from '../../../../utils/utits';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import styles from './CreatorModal.module.css';

type CreateAdminCreatorModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (data: { influencer: WriteAdminInfluencerResponse; costs: InfluencerCost[] }) => void;
};

const CreateAdminCreatorModal = ({ isOpen, onClose, onCreated }: CreateAdminCreatorModalProps) => {
    const { currencies } = useNonNullContext(OptionsContext);

    const initialValues: AdminCreatorFormValues = {
        name: '',
        email: '',
        manager: null,
        is_available_for_booking: true,
        manager_commission: 2.5,
        agency_commission: 17.5,
        payment_terms: paymentTermOptions.find((o) => o.value === 'client_terms'),
        preferred_payment_method: preferredPaymentOptions.find((o) => o.value === 'bank_transfer'),
    };

    const handleSubmit = useCallback(
        async (values: AdminCreatorFormValues, { setSubmitting, setErrors }: FormikHelpers<AdminCreatorFormValues>) => {
            try {
                setSubmitting(true);
                const influencerResponse = await postAdminRetrieveInfluencer({
                    name: values.name,
                    tiktok_username: values.tiktok_username || null,
                    instagram_username: values.instagram_username || null,
                    youtube_username: values.youtube_username || null,
                    agency_commission:
                        typeof values.agency_commission === 'number' ? values.agency_commission / 100 : 0,
                    manager_commission:
                        typeof values.manager_commission === 'number' ? values.manager_commission / 100 : 0,
                    content_tags: values.content_tags?.length
                        ? values.content_tags?.map((tag) => Number(tag.value))
                        : [],
                    location: values.location?.value ?? null,
                    first_name: values.first_name,
                    last_name: values.last_name,
                    phone: values.phone,
                    email: values.email,
                    birthday: values.birthday ? values.birthday.split('/').reverse().join('-') : null,
                    address_line_1: values.address_line_1,
                    address_region: values.address_region,
                    address_city: values.address_city,
                    address_postcode: values.address_postcode,
                    bank_name: values.bank_name,
                    bank_account_number: values.bank_account_number,
                    bank_sort_code: values.bank_sort_code,
                    payment_terms: values.payment_terms?.value,
                    preferred_currency: values.preferred_currency?.value ?? null,
                    is_available_for_booking: values.is_available_for_booking,
                    paypal_email_address: values.paypal_email_address,
                    preferred_payment_method: values.preferred_payment_method?.value,
                    manager: values?.manager?.value,
                    contact_details: values?.contact_details,
                    roster_share_agency: values?.roster_share_agency?.value ?? null,
                });

                if (influencerResponse.status === 400) {
                    setErrors(mapApiErrorsToFormikErrors(influencerResponse.data));
                    return;
                }

                const influencer = influencerResponse.data;
                let costs: InfluencerCost[] = [];
                if (values.costs) {
                    const nonNullCostValues = omitBy(values.costs, isNil);
                    const requests = [];
                    for (const platform in nonNullCostValues) {
                        for (const campaignType in nonNullCostValues[platform]) {
                            for (const cost in nonNullCostValues[platform][campaignType]) {
                                if (cost === '') {
                                    continue;
                                }
                                const currencyId = currencies.find((curr) => curr.name === cost.toUpperCase())?.id;
                                if (!currencyId) {
                                    throw new Error('Couldnt find requested currency');
                                }

                                if (isPlatform(platform) && isCampaignType(campaignType)) {
                                    requests.push(
                                        postAdminInfluencerCost({
                                            influencer: influencerResponse.data.id,
                                            cost: Number(nonNullCostValues[platform][campaignType][cost]),
                                            campaign_type: campaignType,
                                            platform: platform,
                                            currency: currencyId,
                                        })
                                    );
                                } else {
                                    throw new Error('supplied form values did not meet type specification');
                                }
                            }
                        }
                    }

                    costs = (await Promise.all(requests))
                        .filter((response): response is ApiResponse<InfluencerCost, 201> => response.status === 201)
                        .map((response) => response.data);
                }

                onClose();
                onCreated({ influencer, costs });
            } catch {
                showNotification('Could not perform operation', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [currencies, onClose, onCreated]
    );

    return (
        <Modal className={styles.modal} isOpen={isOpen} onClose={onClose} closeOnOverlayClick>
            <AdminCreatorForm handleSubmit={handleSubmit} initialValues={initialValues} />
        </Modal>
    );
};

export default CreateAdminCreatorModal;
